<template>
  <div class="locale" @click.stop="">
    <button type="button" class="locale__pill" @click="open">
      <span class="locale__flag" v-html="getLangFlag(currentCode)"></span>
      <span class="locale__code">{{ currentCode }}</span>
      <svg class="locale__caret" width="11" height="6" viewBox="0 0 11 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.5973 0.236437C10.4567 0.0958343 10.2659 0.0168481 10.0671 0.0168481C9.86818 0.0168481 9.67745 0.0958343 9.5368 0.236437L5.8243 3.94894L2.1118 0.236437C1.97035 0.0998192 1.7809 0.0242233 1.58425 0.0259323C1.38761 0.0276408 1.1995 0.106517 1.06044 0.245574C0.921383 0.384629 0.842506 0.572739 0.840798 0.769387C0.839089 0.966034 0.914686 1.15549 1.0513 1.29694L5.29405 5.53969C5.4347 5.68029 5.62543 5.75928 5.8243 5.75928C6.02318 5.75928 6.21391 5.68029 6.35455 5.53969L10.5973 1.29694C10.7379 1.15629 10.8169 0.965561 10.8169 0.766688C10.8169 0.567814 10.7379 0.377083 10.5973 0.236437Z"/>
      </svg>
    </button>

    <!-- Bottom sheet — teleported to <body> in mounted() so no transformed /
         backdrop-filtered ancestor can break its fixed positioning. -->
    <div ref="sheetRoot" v-show="isOpen" class="locale-portal">
      <div class="locale-portal__backdrop" :class="{ 'is-in': sheetIn }" @click="close"></div>
      <div class="locale-portal__sheet"
           :class="{ 'is-in': sheetIn }"
           :style="sheetDragStyle"
           @touchstart="onSheetTouchStart"
           @touchmove="onSheetTouchMove"
           @touchend="onSheetTouchEnd"
           @touchcancel="onSheetTouchEnd">
        <div class="locale-portal__handle"></div>
        <button type="button"
                class="locale-portal__close"
                :aria-label="$t('Close')"
                @click="close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
          </svg>
        </button>
        <h3 class="locale-portal__title">{{ $t('Language') }}</h3>
        <ul ref="list" class="locale-portal__list">
          <li v-for="language in availableLanguages"
              :key="language.code"
              class="locale-portal__item"
              :class="{ 'is-active': language.code === currentCode }"
              @click="handleSetLocale(language.code)">
            <span class="locale-portal__flag" v-html="getLangFlag(language.code)"></span>
            <span class="locale-portal__name">{{ language.name }}</span>
            <svg v-if="language.code === currentCode" class="locale-portal__check" width="18" height="18" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from "@/i18n";
import {BASE_API_URL} from "@/appConstants";
import axios from "axios";
import router from "@/router/router";
import { lockNav, unlockNav } from "@/utils/navLock";

export default {
  name: "VAppLocale",
  props: {
    value: {type: Boolean, required: false}
  },
  emits: ['update:value'],
  data() {
    return {
      isOpen: false,
      sheetIn: false,
      sheetDragging: false,
      sheetDragY: 0,
    }
  },
  computed: {
    availableLanguages() {
      return this.$store.state.companyData.languages || [];
    },
    currentCode() {
      return this.$store.state.prefLangCode || 'en';
    },
    computedCurrentLocale() {
      let locale = null;
      const prefLangCode = this.currentCode;
      this.availableLanguages.forEach((lang) => {
        if (lang.code === prefLangCode) {
          locale = lang;
        }
      });
      return locale;
    },
    sheetDragStyle() {
      // While a finger drags the sheet, pin it to the finger with no transition;
      // once released the inline style drops away and the class transition takes
      // over (back to open, or down past the edge via close()).
      if (!this.sheetDragging) return null;
      return { transform: `translateY(${this.sheetDragY}px)`, transition: 'none' };
    },
  },
  mounted() {
    if (!this.$store.state.prefLangCode) {
      let browserLanguage = navigator.language || navigator.userLanguage;
      let languageCode = browserLanguage.substring(0, 2);

      this.availableLanguages.forEach((lang) => {
        if (lang.code === languageCode) {
          this.handleSetLocale(languageCode);
        }
      });
    }

    // Move the sheet to <body> so fixed positioning is relative to the viewport
    // regardless of any transformed / backdrop-filtered ancestor (hero, headers).
    if (this.$refs.sheetRoot) {
      document.body.appendChild(this.$refs.sheetRoot);
    }
  },
  beforeDestroy() {
    if (this.$refs.sheetRoot && this.$refs.sheetRoot.parentNode) {
      this.$refs.sheetRoot.parentNode.removeChild(this.$refs.sheetRoot);
    }
  },
  methods: {
    open() {
      this.isOpen = true;
      lockNav();
      this.$nextTick(() => {
        window.requestAnimationFrame(() => { this.sheetIn = true; });
      });
    },
    close() {
      this.sheetIn = false;
      unlockNav();
      setTimeout(() => { this.isOpen = false; }, 280);
    },
    onSheetTouchStart(e) {
      if (!this.sheetIn) return;
      const t = e.touches[0];
      this._dragActive = false;
      this._sheetStartY = t.clientY;
      this._sheetLastY = t.clientY;
      this._sheetLastT = e.timeStamp;
      this._sheetVelocity = 0;
      // Touches that begin inside the language list belong to the list's own
      // scroll until it is pinned to the top — only then may they drag the sheet.
      this._fromList = !!(this.$refs.list && this.$refs.list.contains(e.target));
    },
    onSheetTouchMove(e) {
      if (!this.sheetIn) return;
      const t = e.touches[0];

      if (!this._dragActive) {
        const movingDown = t.clientY > this._sheetLastY;
        const listAtTop = !this._fromList || !this.$refs.list || this.$refs.list.scrollTop <= 0;
        if (movingDown && listAtTop) {
          // Take the gesture over from here so the sheet follows without a jump.
          this._dragActive = true;
          this._sheetStartY = t.clientY;
          this.sheetDragging = true;
          this.sheetDragY = 0;
        } else {
          this._sheetLastY = t.clientY;
          this._sheetLastT = e.timeStamp;
          return; // native list scroll
        }
      }

      const delta = t.clientY - this._sheetStartY;
      // Follow the finger downward only — the sheet has nowhere to go up.
      this.sheetDragY = delta > 0 ? delta : 0;
      const dt = e.timeStamp - this._sheetLastT;
      if (dt > 0) this._sheetVelocity = (t.clientY - this._sheetLastY) / dt;
      this._sheetLastY = t.clientY;
      this._sheetLastT = e.timeStamp;
      if (delta > 0) e.preventDefault();
    },
    onSheetTouchEnd() {
      if (!this._dragActive) return;
      this._dragActive = false;
      this.sheetDragging = false;
      // Close on a long pull or a quick downward flick; otherwise spring back.
      const shouldClose = this.sheetDragY > 96
        || (this.sheetDragY > 24 && this._sheetVelocity > 0.45);
      this.sheetDragY = 0;
      if (shouldClose) this.close();
    },
    handleSetLocale(locale) {
      i18n.locale = locale;
      this.$store.commit('setPrefLangCode', locale);

      this.updateServerTokenLanguage();
      this.reloadCompanyData();

      this.$emit('update:value', false);
      this.close();
    },
    reloadCompanyData() {
      var endpoint = BASE_API_URL +
          'locale?code=' + this.$store.state.companyName +
          '&identifier=' + this.$store.getters.getFingerprint +
          '&pref_lang_code=' + this.$store.state.prefLangCode;

      if (this.$store.state.tableId) endpoint += '&table_id=' + this.$store.state.tableId;

      axios.get(endpoint)
          .then(response => {
            this.companyData = response.data;
            this.$store.commit('saveCompanyData', this.companyData);
          })
          .catch(error => {
            router.push('error');
            console.log(error);
          })
    },
    updateServerTokenLanguage() {
      const endpoint = BASE_API_URL +
          'changelanguage?token=' + this.$store.state.companyData.token +
          '&lang_code=' + this.$store.state.prefLangCode;
      axios.get(endpoint)
          .then()
          .catch(error => {
            router.push('error');
            console.log(error);
          })
    },
    getLangFlag(code) {
      switch (code) {
        case 'en': return '🇬🇧';
        case 'pl': return '🇵🇱';
        case 'de': return '🇩🇪';
        case 'ru': return '🇷🇺';
        case 'uk': return '🇺🇦';
        case 'ko': return '🇰🇷';
        case 'es': return '🇪🇸';
        case 'it': return '🇮🇹';
        case 'fr': return '🇫🇷';
        case 'tr': return '🇹🇷';
        case 'he': return '🇮🇱';
        case 'cs': return '🇨🇿';
        case 'lt': return '🇱🇹';
        default: return '🇬🇧';
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.locale {
  position: relative;
}

/* Trigger pill — consumers (e.g. Welcome hero) can re-skin via ::v-deep .locale__pill */
.locale__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 13px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  background: var(--surface-card);
  color: var(--text-primary);
  cursor: pointer;
  font-family: var(--font-sans);
  -webkit-tap-highlight-color: transparent;
}

.locale__flag {
  font-size: 18px;
  line-height: 1;
}

.locale__code {
  font-size: 13px;
  font-weight: var(--weight-bold);
  text-transform: uppercase;
}

.locale__caret path {
  fill: currentColor;
}

/* ---------- Bottom sheet (teleported to body) ---------- */
.locale-portal {
  position: fixed;
  inset: 0;
  /* Above the sticky page headers (z-index 1100) so the scrim dims everything —
     including the header — and only the sheet sits above it. */
  z-index: 2000;
}

.locale-portal__backdrop {
  position: absolute;
  inset: 0;
  background: var(--overlay-scrim);
  opacity: 0;
  transition: opacity 280ms var(--ease-standard);

  &.is-in { opacity: 1; }
}

.locale-portal__sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 80%;
  /* The sheet itself no longer scrolls — only the list does — so the handle,
     title and close button stay pinned and any touch on them drags the sheet. */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 20px calc(24px + var(--safe-bottom, 0px));
  background: var(--surface-card);
  border-radius: var(--radius-sheet) var(--radius-sheet) 0 0;
  box-shadow: var(--shadow-xl);
  transform: translateY(100%);
  transition: transform 320ms var(--ease-out);

  &.is-in { transform: translateY(0); }
}

.locale-portal__handle {
  width: 42px;
  height: 5px;
  margin: 0 auto 14px;
  flex-shrink: 0;
  border-radius: var(--radius-pill);
  background: var(--border-default);
}

.locale-portal__close {
  position: absolute;
  top: 14px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-pill);
  background: var(--surface-subtle);
  color: var(--text-secondary);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform var(--dur-fast) var(--ease-standard);

  &:active { transform: scale(0.94); }
}

.locale-portal__title {
  margin: 0 0 14px;
  font-size: var(--text-xl);
  font-weight: var(--weight-black);
  color: var(--text-primary);
}

.locale-portal__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
  overflow-y: auto;
  /* Don't let a scrolled-to-the-edge list hand the gesture to the page behind. */
  overscroll-behavior: contain;
}

.locale-portal__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  border-radius: var(--radius-2xl);
  background: var(--surface-subtle);
  cursor: pointer;
  transition: background var(--dur-base) var(--ease-standard);

  &.is-active {
    background: var(--color-primary-soft);
  }
}

.locale-portal__flag {
  font-size: 22px;
  line-height: 1;
}

.locale-portal__name {
  flex: 1;
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  color: var(--text-primary);
}

.locale-portal__check {
  color: var(--color-primary);
  flex-shrink: 0;
}
</style>
